import {
  Badge,
  Button,
  Col,
  EditIcon,
  InfoIcon,
  ListTable,
  RemoveRedEyeOutlinedIcon,
  Row,
  Text,
  Tooltip,
} from 'elmo-elements';
import { BadgeType } from 'elmo-elements/dist/Badge';
import { findIndex } from 'lodash';
import React from 'react';
import { generatePath, Link, useHistory, useLocation } from 'react-router-dom';

import ElaPermissions from '@/lib/auth/permissions/permission.type';
import { useUserHasCapabilities } from '@/lib/auth/useUserHasCapabilities';
import config from '@/lib/config';
import { appData } from '@/lib/data';
import { Translations } from '@/lib/translation';
import {
  generateReviewUrl,
  getQueryString,
  isElearningPublishedToReview,
  openPlayerUIWindow,
  pushParamsToUrl,
} from '@/lib/util';
import {
  useGetCurrentTenantProfileQuery,
  useGetElearningAllowedCategoriesQuery,
  useGetMyLibraryQuery,
} from '@/modules/services';
import {
  ElearningStatus,
  PublishStatus,
  StatusMetadataType,
  SyncStatus,
} from '@/modules/types/elearning';
import ModuleRoutes from '@/pages/Module/routes';
import ImportStatusBadge from './ImportStatusBadge';
import './index.scss';
import { ElearningPublishChannel } from '@/modules/types/elearning/publish.enum';
import { useFlags } from 'launchdarkly-react-client-sdk';

const MyLibraryList = (): React.ReactElement => {
  const location = useLocation();
  const history = useHistory();
  const canEdit = useUserHasCapabilities([ElaPermissions.EDIT_ELEARNING]);
  const {
    learningElaCourseLibraryAdmin: isElaCourseLibraryAdminEnabled,
    learningElaBreatheIntegration: isBreatheFlag,
  } = useFlags();

  const currentQueryString = getQueryString(location.search, {
    ...appData.pagination.defaultParams,
    sort: 'ma',
    order: 'DESC',
  });

  const { data: tenantProfileData } = useGetCurrentTenantProfileQuery();
  const isBreatheEnabled =
    isBreatheFlag && tenantProfileData?.configMetadata?.enableBreathe;
  const { data: allowedCategoriesData } =
    useGetElearningAllowedCategoriesQuery();

  // No interval polling of the paginated list (LMS2-5049): rows that are mid-import
  // render an ImportStatusBadge which polls the lightweight per-elearning status
  // endpoint, and the list is re-fetched once when an import settles.
  const { data, refetch } = useGetMyLibraryQuery(currentQueryString, {
    skip: !allowedCategoriesData,
  });

  const pageSize = 10;
  const tableColumns = {
    name: { label: 'ELMO Module' },
    status: { label: 'Status' },
    modifiedDate: { label: 'Last Modified' },
    page: { label: 'Page' },
  };

  const sortableColumns = [
    { key: 'name', name: 'ELMO Module' },
    { key: 'status', name: 'Status' },
    { key: 'ma', name: 'Last Modified' },
  ];

  const { sort, order } = currentQueryString;
  const sortedColumnIndex = findIndex(sortableColumns, { key: sort });

  const getStatus = (elearningId: string, statusMeta: StatusMetadataType) => {
    // Show import-specific status first — it overrides the standard publish status.
    // A mid-import row delegates to ImportStatusBadge, which polls the lightweight
    // status endpoint for this elearning only and asks for one list re-fetch when
    // the import settles.
    if (
      statusMeta.importStatus === 'importing' ||
      statusMeta.importStatus === 'queued'
    ) {
      return (
        <ImportStatusBadge
          elearningId={elearningId}
          initialStatus={statusMeta.importStatus}
          onSettled={refetch}
        />
      );
    }

    if (statusMeta.importStatus === 'failed') {
      return <Badge type="danger">Import failed</Badge>;
    }

    const badgeData = { text: '', type: '' };
    switch (true) {
      case statusMeta.lmsStatus >= PublishStatus.published:
        badgeData.text = 'Live';
        badgeData.type = 'info';
        break;
      case statusMeta.scormStatus === PublishStatus.published &&
        !!tenantProfileData?.scorm:
        badgeData.text = 'Scorm';
        badgeData.type = 'info';
        break;
      case statusMeta.status >= ElearningStatus.Review:
        badgeData.text = 'Review';
        badgeData.type = 'warning';
        break;
      default:
        badgeData.text = 'Draft';
        badgeData.type = 'grey';
        break;
    }

    return <Badge type={badgeData.type as BadgeType}>{badgeData.text}</Badge>;
  };

  const getChannelType = (type: ElearningPublishChannel) => {
    switch (type) {
      case ElearningPublishChannel.Generic:
        return 'ELMO Learning';
      case ElearningPublishChannel.CourseLibrary:
        return 'Course Library';
      case ElearningPublishChannel.VideoLibrary:
        return 'Video Library';
      case ElearningPublishChannel.BREATHE:
        return 'Breathe';
      default:
        return '';
    }
  };

  const getSyncStatus = (statusMeta: StatusMetadataType) => {
    const returnWarning = (
      <Tooltip
        title={Translations.myElearnings.warning.unpublishedChanges.default}
        placement="bottom"
      >
        <Text color="warning">
          <InfoIcon />
        </Text>
      </Tooltip>
    );

    const isReviewUnsynced =
      statusMeta.status >= ElearningStatus.Review &&
      statusMeta.courseSync === SyncStatus.unsynced;
    const isElmoLearningUnsynced =
      statusMeta.lmsStatus >= PublishStatus.published &&
      statusMeta.lmsSync === SyncStatus.unsynced;
    const isScormUnsynced =
      statusMeta.scormStatus === PublishStatus.published &&
      statusMeta.scormSync === SyncStatus.unsynced;
    const isBreatheUnsynced =
      statusMeta.breatheStatus === PublishStatus.published &&
      statusMeta.breatheSync === SyncStatus.unsynced;

    if (
      isReviewUnsynced ||
      isElmoLearningUnsynced ||
      isScormUnsynced ||
      isBreatheUnsynced
    )
      return returnWarning;

    return '';
  };

  const handleSort = (column: number, direction: string) => {
    pushParamsToUrl(
      {
        sort: sortableColumns[column].key,
        order: direction.toUpperCase(),
        page: 1,
      },
      history,
    );
  };

  if (!data) {
    return <div />;
  }

  return (
    <ListTable
      id="MyLibraryTable"
      ariaLabel="List of My Library Modules"
      sortColumn={sortedColumnIndex}
      sortDirection={order.toLowerCase()}
      onSort={handleSort}
    >
      <ListTable.Header>
        <ListTable.Column label={tableColumns.name.label} sortable={true} />
        <ListTable.Column label={tableColumns.status.label} />
        <ListTable.Column
          label={tableColumns.modifiedDate.label}
          sortable={true}
        />
      </ListTable.Header>
      <ListTable.Body>
        {data.data.map((elearning: any, index: number) => {
          const elearningPublishStatus = elearning?.statusMetadata;
          const previewTarget = 'review';
          const isPublishedToReview = !!(
            elearningPublishStatus?.status &&
            isElearningPublishedToReview(elearningPublishStatus.status)
          );
          const optionsDate: Intl.DateTimeFormatOptions = {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          };
          const optionsTime: Intl.DateTimeFormatOptions = {
            hour: 'numeric',
            minute: 'numeric',
            hour12: true,
          };
          const formattedDate = new Date(
            elearning.modifiedAt,
          ).toLocaleDateString('en-US', optionsDate);
          const formattedTime = new Date(
            elearning.modifiedAt,
          ).toLocaleTimeString('en-US', optionsTime);
          const finalFormattedDate = `${formattedDate}, ${formattedTime.toLowerCase()}`;
          const isLive =
            elearningPublishStatus.lmsStatus >= PublishStatus.published ||
            Boolean(elearning.publishedToLiveAt);
          const isBreathe =
            elearningPublishStatus.breatheStatus === PublishStatus.published;

          let reviewlink = '';
          if (isPublishedToReview) {
            reviewlink = generateReviewUrl(
              elearning,
              config.OldCBURL,
              config.OldCBTOKEN,
            );
          }
          const moduleLink = generatePath(ModuleRoutes.path, {
            elearningId: elearning.id,
          });
          return (
            <ListTable.Tr
              data-testid="list-table-item"
              key={elearning.id}
              icon={
                <>
                  {canEdit && (
                    <Link to={moduleLink}>
                      <Button
                        icon={<EditIcon />}
                        ariaLabel="Edit This Module"
                        tooltip={{ title: 'Edit This Module' }}
                      />
                    </Link>
                  )}
                  {canEdit !== undefined && !canEdit && isPublishedToReview && (
                    <Button
                      icon={<RemoveRedEyeOutlinedIcon />}
                      ariaLabel="Preview This Module"
                      onClick={() => {
                        openPlayerUIWindow(
                          elearning,
                          reviewlink,
                          previewTarget,
                          appData.playerTheme.default.windowMetadata,
                        );
                      }}
                      tooltip={{ title: 'Preview This Module' }}
                    />
                  )}
                  {canEdit !== undefined &&
                    !canEdit &&
                    !isPublishedToReview && (
                      <Button
                        icon={<InfoIcon />}
                        ariaLabel="Course not published to review"
                        onClick={() => {}}
                        tooltip={{ title: 'Course not published to review' }}
                      />
                    )}
                </>
              }
            >
              <ListTable.Td>
                <div className="elmo-module-name">
                  {canEdit ? (
                    <Link to={moduleLink}>{elearning.name}</Link>
                  ) : (
                    <span>{elearning.name}</span>
                  )}
                  {((isLive && isElaCourseLibraryAdminEnabled) ||
                    (isBreathe && isBreatheEnabled)) && (
                    <span className="elmo-module-name__channels">
                      <p className="elmo-module-name__label">Channels</p>
                      {getChannelType(
                        elearning.type as ElearningPublishChannel,
                      )}
                    </span>
                  )}
                </div>
              </ListTable.Td>
              <ListTable.Td>
                <Row>
                  <Col span={16}>
                    {getStatus(elearning.id, elearning.statusMetadata)}
                  </Col>
                  <Col span={8}>{getSyncStatus(elearning.statusMetadata)}</Col>
                </Row>
              </ListTable.Td>
              <ListTable.Td>{finalFormattedDate}</ListTable.Td>
            </ListTable.Tr>
          );
        })}
      </ListTable.Body>
      <ListTable.Pagination
        hideDropdown={true}
        pageSize={pageSize}
        currentPage={data?.meta.page || 1}
        onPageChange={(pageNumber) => {
          /* This is intentional */
          pushParamsToUrl({ page: pageNumber }, history);
        }}
        onPageSizeChange={() => {}}
        totalResults={data?.meta.itemCount}
      />
    </ListTable>
  );
};

export default MyLibraryList;
